/**
 * Shared EPSG:27700 (British National Grid) <-> WGS84 proj4 conversion,
 * VISUAL-ONLY. Consolidates a definition previously duplicated across
 * core/route.ts (GPX route ribbon) and ui/osmProjection.ts (OSM minimap),
 * where the duplication was only safe as long as both copies stayed
 * byte-identical (issue #40).
 *
 * Accuracy: this is a 7-parameter Helmert transform, not the OSTN15 grid
 * shift the pipeline uses elsewhere in raypath/. Planar error is roughly
 * 1-3 m versus OSTN15 ground truth. That is fine for a route ribbon or a
 * minimap marker, but this module must NEVER be used for LOS or any other
 * analytical work — core/coordinates.ts (bngToLocal/localToBng) is the
 * single source of truth there and stays fully independent of proj4
 * (hard constraint #1 and #4, GOAL.md).
 */
import proj4 from 'proj4';

export const ACCURACY_NOTE =
  'proj4 EPSG:27700 here is a 7-parameter Helmert transform, not the OSTN15 ' +
  'grid shift the pipeline uses elsewhere; planar error is roughly 1-3 m. ' +
  'Visual-only (route ribbon, OSM minimap) — never use for LOS or other ' +
  'analytical work; core/coordinates.ts is the source of truth there.';

// proj4.defs is a global registry keyed by name; registering the same name
// twice is idempotent, so importing this module from multiple call sites is
// safe.
proj4.defs(
  'EPSG:27700',
  '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 ' +
    '+ellps=airy +towgs84=446.448,-125.157,542.06,0.1502,0.247,0.8421,-20.4894 +units=m +no_defs',
);

const converter = proj4('EPSG:27700', 'EPSG:4326');

/** British National Grid easting/northing (metres) -> WGS84 lon/lat (degrees). Visual only. */
export function bngToWgs84(easting: number, northing: number): { lon: number; lat: number } {
  const [lon, lat] = converter.forward([easting, northing]);
  return { lon, lat };
}

/** WGS84 lon/lat (degrees) -> British National Grid easting/northing (metres). Visual only. */
export function wgs84ToBng(lon: number, lat: number): { easting: number; northing: number } {
  const [easting, northing] = converter.inverse([lon, lat]);
  return { easting, northing };
}
